// The inputs and tools hightlights

// Values are without a border, the variables have a border.
// the lowercase functions are pure html, the uppercase are react components
// (which are the same just with a few UX niceities)

import { CompositeDecorator, ContentBlock, ContentState, EditorState } from "draft-js";
import { classNames } from "lib/utils";
import memoizeOne from "memoize-one";
import memoize from "micro-memoize";
import React from "react";
import { extractInputIndicesFromPrompt, extractToolIndicesFromPrompt } from "./utils";

// Use memoizeOne instead of lodash.memoize because we don't care about caching all previous results.
const memoizedExtractInputIndicesFromPrompt = memoizeOne(extractInputIndicesFromPrompt);
const memoizedExtractToolIndicesFromPrompt = memoizeOne(extractToolIndicesFromPrompt);

// Pure html span so it can be used in react-truncate-markup which does not collapse react components.
export const inputvariable = (children: string, offsetKey?: string, title?: string) => (
  // Some margin and padding does cause a slight layout shift, but I think it's preferable.
  <span
    className="mx-1 rounded-ms border border-sky-500 bg-background-prompt-input px-1 py-2"
    title={title}
    data-offset-key={offsetKey}
  >
    {children}
  </span>
);

export const blockvariable = (children: string, offsetKey?: string, title?: string) => (
  <span className="mx-1 font-bold" title={title} data-offset-key={offsetKey}>
    {children}
  </span>
);

export const errorvariable = (children: string, offsetKey?: string, title?: string) => (
  <span
    className="mx-1 rounded-ms border border-red-500 bg-red-100 px-1 py-2"
    title={`Not a valid variable name`}
    data-offset-key={offsetKey}
  >
    {children}
  </span>
);

export const inputvalue = (children: string, offsetKey?: string, title?: string) => (
  // Some margin and padding does cause a slight layout shift, but I think it's preferable.
  <span className="mx-1 rounded-sm bg-background-prompt-input px-1 py-2" data-offset-key={offsetKey} title={title}>
    {children}
  </span>
);

export const toolvariable = (children: string, offsetKey?: string, title?: string, className?: string) => (
  <span
    className={classNames("mx-1 rounded-ms border border-violet-500 bg-background-prompt-tool px-1 py-2", className)}
    data-offset-key={offsetKey}
    title={title}
  >
    {children}
  </span>
);
export const toolvalue = (children: string, offsetKey?: string, title?: string) => (
  <span className="mx-1 rounded-sm bg-background-prompt-tool px-1 py-2" data-offset-key={offsetKey} title={title}>
    {children}
  </span>
);

const InputVariable = (props: React.PropsWithChildren<{ offsetKey: string }>) => {
  return (
    <React.Fragment key={props.offsetKey}>{inputvariable(props.children as string, props.offsetKey)}</React.Fragment>
  );
};

export const ToolVariable = (
  props: React.PropsWithChildren<{
    offsetKey: string;
    className?: string;
  }>,
) => {
  return (
    <React.Fragment key={props.offsetKey}>
      {toolvariable(props.children as string, props.offsetKey, "", props.className)}
    </React.Fragment>
  );
};

function inputStrategy(
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState,
) {
  memoizedExtractInputIndicesFromPrompt(contentBlock.getText()).forEach((match) => {
    callback(match.start, match.end);
  });
}

function toolStrategy(
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState,
) {
  memoizedExtractToolIndicesFromPrompt(contentBlock.getText()).forEach((match) => {
    callback(match.start, match.end);
  });
}

const PLAYGROUND_EDITOR_DECORATORS = new CompositeDecorator([
  { strategy: inputStrategy, component: InputVariable },
  { strategy: toolStrategy, component: ToolVariable },
]);

// text || "" is to handle the case where the prompt_template is null (for chat endpoints).
// TODO: Consider if we need to keep that when adding chat playground.
const createEditorState_ = (text: string | null, useDecorators: boolean): EditorState => {
  return EditorState.createWithContent(
    ContentState.createFromText(text || ""),
    useDecorators ? PLAYGROUND_EDITOR_DECORATORS : undefined,
  );
};

export const createEditorState = memoize(createEditorState_, { maxSize: 100 });
